import React from 'react';
import { X, Mail, CheckCircle, Clock, AlertOctagon, Users } from 'lucide-react';

interface ConsentCategorizationData {
    totalDrivers: number;
    categorization: {
        needsConsent: number;
        hasValidConsent: number;
        hasPendingConsent: number;
        noEmail: number;
    };
    willSend: number;
    willSkip: number;
}

interface ConsentCategorizationModalProps {
    isOpen: boolean;
    onClose: () => void;
    categorization: ConsentCategorizationData | null;
    onProceed: () => void;
    isLoading?: boolean;
}

const ConsentCategorizationModal: React.FC<ConsentCategorizationModalProps> = ({
    isOpen,
    onClose,
    categorization,
    onProceed,
    isLoading = false
}) => {
    if (!isOpen || !categorization) return null;

    const { totalDrivers, categorization: cat, willSend, willSkip } = categorization;

    return (
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
                {/* Header */}
                <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                    <div className="flex items-center space-x-4">
                        <div className="p-2.5 rounded-lg bg-blue-50">
                            <Mail className="h-5 w-5 text-blue-600" />
                        </div>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900">
                                Consent Forms Preview
                            </h2>
                            <p className="text-sm text-gray-500 mt-0.5">
                                Review driver breakdown before sending
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X className="h-5 w-5" />
                    </button>
                </div>

                {/* Body */}
                <div className="flex-1 overflow-y-auto px-6 py-5 space-y-4">
                    {/* Summary Stats */}
                    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center space-x-6 text-sm">
                            <div className="flex items-center space-x-2">
                                <Users className="w-4 h-4 text-gray-400" />
                                <span className="text-gray-600">Total selected:</span>
                                <span className="font-semibold text-gray-900">{totalDrivers}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-2.5 h-2.5 rounded-full bg-blue-600"></div>
                                <span className="text-gray-600">Will send:</span>
                                <span className="font-semibold text-gray-900">{willSend}</span>
                            </div>
                            <div className="flex items-center space-x-2">
                                <div className="w-2.5 h-2.5 rounded-full bg-gray-400"></div>
                                <span className="text-gray-600">Will skip:</span>
                                <span className="font-semibold text-gray-900">{willSkip}</span>
                            </div>
                        </div>
                    </div>

                    {/* Categorization Breakdown */}
                    <div className="space-y-3">
                        {/* Will Send - New Consent */}
                        {cat.needsConsent > 0 && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-3 bg-blue-50 border-b border-blue-100">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-2.5 h-2.5 rounded-full bg-blue-600"></div>
                                        <div className="flex items-center space-x-2">
                                            <Mail className="w-4 h-4 text-blue-600" />
                                            <span className="font-medium text-gray-900">New Consent Forms</span>
                                        </div>
                                    </div>
                                    <span className="text-sm font-semibold text-blue-600">
                                        {cat.needsConsent} driver{cat.needsConsent !== 1 ? 's' : ''}
                                    </span>
                                </div>
                                <div className="px-4 py-3 bg-white">
                                    <p className="text-sm text-gray-600">
                                        {cat.needsConsent} driver{cat.needsConsent !== 1 ? 's' : ''} will receive a new consent form (no consent or expired consent)
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Will Send - Reminders */}
                        {cat.hasPendingConsent > 0 && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-3 bg-amber-50 border-b border-amber-100">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-2.5 h-2.5 rounded-full bg-amber-500"></div>
                                        <div className="flex items-center space-x-2">
                                            <Clock className="w-4 h-4 text-amber-500" />
                                            <span className="font-medium text-gray-900">Consent Reminders</span>
                                        </div>
                                    </div>
                                    <span className="text-sm font-semibold text-amber-600">
                                        {cat.hasPendingConsent} driver{cat.hasPendingConsent !== 1 ? 's' : ''}
                                    </span>
                                </div>
                                <div className="px-4 py-3 bg-white">
                                    <p className="text-sm text-gray-600">
                                        {cat.hasPendingConsent} driver{cat.hasPendingConsent !== 1 ? 's' : ''} will receive a reminder (pending consent not yet provided)
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Will Skip - Valid Consent */}
                        {cat.hasValidConsent > 0 && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-3 bg-green-50 border-b border-green-100">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-2.5 h-2.5 rounded-full bg-green-600"></div>
                                        <div className="flex items-center space-x-2">
                                            <CheckCircle className="w-4 h-4 text-green-600" />
                                            <span className="font-medium text-gray-900">Valid Consent (Skipped)</span>
                                        </div>
                                    </div>
                                    <span className="text-sm font-semibold text-gray-600">
                                        {cat.hasValidConsent} driver{cat.hasValidConsent !== 1 ? 's' : ''}
                                    </span>
                                </div>
                                <div className="px-4 py-3 bg-white">
                                    <p className="text-sm text-gray-600">
                                        {cat.hasValidConsent} driver{cat.hasValidConsent !== 1 ? 's' : ''} already {cat.hasValidConsent === 1 ? 'has' : 'have'} valid consent and will be skipped
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Will Skip - No Email */}
                        {cat.noEmail > 0 && (
                            <div className="border border-gray-200 rounded-lg overflow-hidden">
                                <div className="flex items-center justify-between px-4 py-3 bg-red-50 border-b border-red-100">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-2.5 h-2.5 rounded-full bg-red-600"></div>
                                        <div className="flex items-center space-x-2">
                                            <AlertOctagon className="w-4 h-4 text-red-600" />
                                            <span className="font-medium text-gray-900">No Email Address (Skipped)</span>
                                        </div>
                                    </div>
                                    <span className="text-sm font-semibold text-gray-600">
                                        {cat.noEmail} driver{cat.noEmail !== 1 ? 's' : ''}
                                    </span>
                                </div>
                                <div className="px-4 py-3 bg-white">
                                    <p className="text-sm text-gray-600">
                                        {cat.noEmail} driver{cat.noEmail !== 1 ? 's have' : ' has'} no email address and cannot receive consent forms
                                    </p>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Info message if no drivers will be sent */}
                    {willSend === 0 && (
                        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                            <p className="text-sm text-amber-800">
                                No consent forms will be sent. All selected drivers either have valid consent or cannot receive emails.
                            </p>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        {willSend > 0 ? (
                            <>
                                <span className="font-semibold text-gray-900">{willSend}</span> driver{willSend !== 1 ? 's' : ''} will receive consent form{willSend !== 1 ? 's' : ''}
                            </>
                        ) : (
                            <span className="text-gray-500">No drivers will be sent consent forms</span>
                        )}
                    </div>
                    <div className="flex space-x-3">
                        <button
                            onClick={onClose}
                            disabled={isLoading}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            Cancel
                        </button>
                        <button
                            onClick={onProceed}
                            disabled={willSend === 0 || isLoading}
                            className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:brightness-110 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
                        >
                            {isLoading ? 'Sending...' : `Send to ${willSend} Driver${willSend !== 1 ? 's' : ''}`}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ConsentCategorizationModal;
